import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../utils/api";
import {
  canOpen,
  channelsInDisplayOrder,
  normaliseItems,
  readStoredUser,
} from "../utils/featureAccess";
import "./GoToPage.css";

// Resolves a channel-less link such as /go/channelgrid.
//
// A page can be sent to anyone; a channel cannot, since two customers rarely
// hold the same one. So the link names only the page, and this picks the
// channel: walking the recipient's own list in order, the first channel on
// which that page is open.

export default function GoToPage() {
  const { page } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("resolving"); // resolving | none | error

  useEffect(() => {
    let cancelled = false;
    const user = readStoredUser();
    const key = String(page || "").toLowerCase();
    const ids = channelsInDisplayOrder(user);

    // Settled before any request: if the page is not on the user's own account,
    // no channel of theirs can open it, however many they have.
    if (!key || ids.length === 0 || !normaliseItems(user.sidebaritems).includes(key)) {
      setStatus("none");
      return;
    }

    api
      .get(`/ci?i=${ids.join("-")}`)
      .then((res) => {
        if (cancelled) return;

        const infoById = new Map(
          (Array.isArray(res.data) ? res.data : []).map((c) => [String(c.i), c])
        );

        // /ci answers in channel-number order, so the walk follows the user's
        // list rather than the response. A channel missing from the response
        // is inactive and is skipped like one without the page.
        const target = ids.find((id) => {
          const info = infoById.get(id);
          return info && canOpen(user.sidebaritems, info.sidebaritems, key);
        });

        if (!target) {
          setStatus("none");
          return;
        }

        // The channel list stores this before opening a channel; the header
        // reads it for the channel's name and logo.
        const info = infoById.get(target);
        localStorage.setItem(
          "adm_panel_channelInfo_" + target,
          JSON.stringify({ i: info.i, n: info.n, u: info.u })
        );

        navigate(`/channel/${target}?page=${encodeURIComponent(key)}`, {
          replace: true,
        });
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [page, navigate]);

  if (status === "resolving") {
    return <div className="channel-loading">Loading...</div>;
  }

  return (
    <div className="goto-page">
      <div className="goto-card">
        <h2 className="goto-title">
          {status === "none"
            ? "This feature isn't available on your channels"
            : "Something went wrong"}
        </h2>
        <p className="goto-text">
          {status === "none"
            ? "None of the channels on your account has this page enabled. Contact us if you'd like to add it."
            : "We couldn't open this link. Please try again from your channel list."}
        </p>
        <button
          type="button"
          className="goto-button"
          onClick={() => navigate("/home", { replace: true })}
        >
          Go to my channels
        </button>
      </div>
    </div>
  );
}
